import React, { useEffect, useState, useRef, useCallback } from "react";
import MetaTags from "react-meta-tags";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row
} from "reactstrap";
//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb";
import Datatable from "components/Datatables/datatable";
import { post } from "helpers/api_helper";
import "react-datepicker/dist/react-datepicker.css";
import { Filters } from "../filters";
import Maincard from "../components/maincard";
import { runBatchLoader } from "utils/batchLoader";

const isType = 'Orders'
const styleSheet = {
    fontStyle: {
        fontWeight: 'bold',
        background: '#424141',
        padding: '0px 15px',
        color: '#fff'
    }
}

const OrderReport = () => {
    const [rowData, setRowData] = useState(null)
    const [filterData, setFilterData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [pagination, setPagination] = useState({
        limit: 10,
        page: 1,
        search: ''
    })
    const [batchProgress, setBatchProgress] = useState(null)
    const batchCancelRef = useRef(false)
    const isBatchRunningRef = useRef(false)
    const totalRowsRef = useRef(0)
    const lastQueryRef = useRef('')

    const [counts, setCounts] = useState([
        { title: "New Order", key: "new", value: 0 },
        { title: "Redo Order", key: "redo", value: 0 },
        { title: "Correction Order", key: "correction", value: 0 },
        { title: "Total Orders", key: "total", value: 0 },
        { title: "Total Value", key: "total_value", value: 0 }
    ]);

    useEffect(() => {
        if (rowData?.count !== undefined && rowData?.count !== null) {
            totalRowsRef.current = rowData.count;
        }
    }, [rowData]);

    const handleCancelBatch = useCallback(() => {
        batchCancelRef.current = true;
        isBatchRunningRef.current = false;
        setBatchProgress(prev => prev ? ({ ...prev, active: false, stopped: true }) : null);
        setIsLoading(false);
    }, []);

    const handlePaginationChange = useCallback((newPag) => {
        setPagination(prev => {
            if (
                prev.page === newPag.page &&
                prev.limit === newPag.limit &&
                prev.search === newPag.search &&
                Boolean(prev.isAll) === Boolean(newPag.isAll)
            ) {
                return prev;
            }
            return { ...prev, ...newPag };
        });
    }, []);

    const handleFilterChange = useCallback((filters) => {
        setFilterData(filters);
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    // table Column Data
    let tableColumns = [
        {
            dataField: 'id',
            text: '#',
            sort: true
        }, {
            dataField: 'worktype',
            text: 'Order Type',
            formatter: (cell, row) => {
                switch (row.worktype) {
                    case 'new':
                        return <span style={styleSheet.fontStyle} className="bg-success">New</span>
                    case 'redo':
                        return <span style={styleSheet.fontStyle} className="bg-danger">Redo</span>
                    case 'corr':
                    case 'correction':
                        return <span style={styleSheet.fontStyle} className="bg-warning">Correction</span>
                    default:
                        return row.worktype
                }
            },
            csvFormatter: (cell, row) => {
                switch (row.worktype) {
                    case 'new': return 'New'
                    case 'redo': return 'Redo'
                    case 'corr':
                    case 'correction': return 'Correction'
                    default: return row.worktype
                }
            }
        }, {
            dataField: 'order_number',
            text: 'Order Number',
            sort: true,
            formatter: (cell, row) => <span style={styleSheet.fontStyle}>{row.order_number}</span>,
            csvFormatter: (cell, row) => row.order_number
        }, {
            dataField: 'case_no',
            text: 'Case No',
            sort: true,
            formatter: (cell, row) => <span style={styleSheet.fontStyle}>{row.case_no}</span>,
            csvFormatter: (cell, row) => row.case_no
        }, {
            dataField: 'client',
            text: 'Client',
            sort: true
        }, {
            dataField: 'patient_name',
            text: 'Patient',
            sort: true
        }, {
            dataField: 'order_date',
            text: 'Order Date',
            sort: true,
            formatter: (cell, row) => <b>{row.order_date}</b>
        }, {
            dataField: 'due_date',
            text: 'DueDate',
            sort: true,
            formatter: (cell, row) => <b>{row.due_date}</b>
        }, {
            dataField: 'order_value',
            text: 'Amount',
            sort: true,
            formatter: (cell, row) => <b>{parseFloat(row.order_value || 0).toFixed(2)}</b>,
            csvFormatter: (cell, row) => parseFloat(row.order_value || 0).toFixed(2)
        }
    ];

    const loadOrders = useCallback(async () => {
        if (!filterData || Object.keys(filterData).length === 0) return;

        const isAllSelected = Boolean(
            pagination?.isAll ||
            pagination?.limit === 'all' ||
            pagination?.limit === 'All' ||
            (typeof pagination?.limit === 'number' && pagination?.limit > 100)
        );

        if (isAllSelected) {
            lastQueryRef.current = '';
            batchCancelRef.current = true;
            await new Promise(r => setTimeout(r, 60));
            batchCancelRef.current = false;
            isBatchRunningRef.current = true;

            setIsLoading(true);
            try {
                await runBatchLoader({
                    fetchPage: async (page, batchSize) => {
                        const payload = {
                            ...filterData,
                            page,
                            limit: batchSize,
                            searchTerm: pagination?.search || ''
                        };
                        const res = await post('api/reports/orders', payload);
                        return res?.status === 200 ? res.response : {};
                    },
                    batchSize: 100,
                    initialTotal: totalRowsRef.current || 0,
                    onProgress: setBatchProgress,
                    onRowsLoaded: (accumulatedRows, totalCount) => {
                        totalRowsRef.current = totalCount;
                        setRowData(prev => ({
                            ...(prev || {}),
                            count: totalCount,
                            data: accumulatedRows
                        }));
                    },
                    cancelRef: batchCancelRef,
                    runningRef: isBatchRunningRef,
                });
            } catch (err) {
                console.error("Error batch loading orders:", err);
            } finally {
                setIsLoading(false);
            }
        } else {
            const payload = {
                ...filterData,
                page: pagination.page,
                limit: pagination.limit,
                searchTerm: pagination?.search || ''
            };
            const requestKey = `${JSON.stringify(payload)}`;
            if (lastQueryRef.current === requestKey) return;
            lastQueryRef.current = requestKey;

            setIsLoading(true);
            setBatchProgress(null);
            try {
                const res = await post('api/reports/orders', payload);
                if (res?.status === 200) {
                    setRowData(res.response);
                    if (res.response?.count !== undefined) {
                        totalRowsRef.current = res.response.count;
                    }
                }
            } catch (err) {
                console.error("Error loading orders:", err);
            } finally {
                setIsLoading(false);
            }
        }
    }, [filterData, pagination]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    useEffect(() => {
        if (!rowData) return;

        const totalCount = rowData.count !== undefined ? rowData.count : (rowData.data?.length || 0);
        const newCount = rowData.new_count !== undefined
            ? rowData.new_count
            : (rowData.data?.filter(item => item.worktype === 'new')?.length || 0);
        const redoCount = rowData.redo_count !== undefined
            ? rowData.redo_count
            : (rowData.data?.filter(item => item.worktype === 'redo')?.length || 0);
        const correctionCount = rowData.correction_count !== undefined
            ? rowData.correction_count
            : (rowData.data?.filter(item => item.worktype === 'correction' || item.worktype === 'corr')?.length || 0);
        const totalValue = rowData.total_value !== undefined
            ? Number(rowData.total_value).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })
            : (rowData.data?.reduce((sum, item) => sum + (parseFloat(item.order_value) || 0), 0) || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });

        setCounts([
            { title: "New Order", key: "new", value: newCount },
            { title: "Redo Order", key: "redo", value: redoCount },
            { title: "Correction Order", key: "correction", value: correctionCount },
            { title: "Total Orders", key: "total", value: totalCount },
            { title: "Total Value", key: "total_value", value: totalValue },
        ]);
    }, [rowData]);

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags> <title>Dashboard | {isType}</title> </MetaTags>
                <Container fluid>
                    <Breadcrumbs title="Reports" breadcrumbItem={isType} />
                    <Filters callback={handleFilterChange} filterOptions={{
                        order_type: true,
                        city: true,
                        client: true,
                        dates: true,
                        category: true,
                        p_type: true,
                        product: true
                    }} />

                    <Maincard cardItems={counts} loading={isLoading} />

                    <Row>
                        <Col xl="12">
                            <Card>
                                <CardBody>
                                    <Datatable
                                        keyField={'id'}
                                        title={``}
                                        filename={`Orders_Report`}
                                        isAdd={false}
                                        handleAddButton=''
                                        isSearch={true}
                                        excel={true}
                                        csv={false}
                                        columns={tableColumns}
                                        rowsLength={rowData?.count || 0}
                                        rows={rowData?.data || []}
                                        isTableHead={true}
                                        loading={isLoading && (!batchProgress || batchProgress.loaded === 0)}
                                        batchProgress={batchProgress}
                                        onCancelBatch={handleCancelBatch}
                                        batchSize={100}
                                        is_remote={true}
                                        ssr={handlePaginationChange}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default OrderReport
